import type {
  BacklinkHit,
  GraphData,
  GraphFilter,
  LinkSuggestion,
  NoteIndexEntry,
  SearchResult,
  TagNode,
  UnlinkedMention,
  WorkerFileInput,
  WorkerNoteInput,
  WorkerRequest,
  WorkerResponseData,
  WorkerRpcResponse,
} from './api.js';

/** Promise-RPC wrapper around the index worker. One app-wide instance. */
export class IndexClient {
  private worker: Worker;
  private nextId = 1;
  private pending = new Map<
    number,
    { resolve: (d: WorkerResponseData) => void; reject: (e: Error) => void }
  >();
  private changeListeners = new Set<(rev: number) => void>();
  /** Bumps every time the worker reports the index changed. */
  rev = 0;

  constructor() {
    this.worker = new Worker(new URL('./index.worker.ts', import.meta.url), {
      type: 'module',
    });
    this.worker.onmessage = (ev: MessageEvent<WorkerRpcResponse>) => {
      const msg = ev.data;
      if (msg.reqId === -1) {
        if (msg.data?.type === 'indexed') {
          this.rev = msg.data.rev;
          for (const cb of this.changeListeners) cb(this.rev);
        }
        return;
      }
      const p = this.pending.get(msg.reqId);
      if (!p) return;
      this.pending.delete(msg.reqId);
      if (msg.error) p.reject(new Error(msg.error));
      else p.resolve(msg.data as WorkerResponseData);
    };
    // A worker load/runtime failure must reject in-flight calls, not hang the
    // app on the boot spinner forever (bootstrap awaits replaceAll).
    this.worker.onerror = (e) => {
      console.error('[index worker] error', e.message);
      const err = new Error(`index worker failed: ${e.message}`);
      for (const [, pend] of this.pending) pend.reject(err);
      this.pending.clear();
    };
  }

  onChange(cb: (rev: number) => void): () => void {
    this.changeListeners.add(cb);
    return () => this.changeListeners.delete(cb);
  }

  private call(req: WorkerRequest): Promise<WorkerResponseData> {
    const reqId = this.nextId++;
    return new Promise((resolve, reject) => {
      this.pending.set(reqId, { resolve, reject });
      this.worker.postMessage({ reqId, req });
    });
  }

  replaceAll(notes: WorkerNoteInput[], files: WorkerFileInput[]) {
    return this.call({ type: 'replaceAll', notes, files });
  }
  upsertNote(note: WorkerNoteInput) {
    return this.call({ type: 'upsertNote', note });
  }
  setFiles(files: WorkerFileInput[]) {
    return this.call({ type: 'setFiles', files });
  }
  remove(id: string) {
    return this.call({ type: 'remove', id });
  }
  async search(q: string, limit?: number): Promise<SearchResult[]> {
    const d = await this.call({ type: 'search', q, limit });
    return d.type === 'search' ? d.results : [];
  }
  async backlinks(id: string): Promise<BacklinkHit[]> {
    const d = await this.call({ type: 'backlinks', id });
    return d.type === 'backlinks' ? d.hits : [];
  }
  async unlinkedMentions(id: string): Promise<UnlinkedMention[]> {
    const d = await this.call({ type: 'unlinkedMentions', id });
    return d.type === 'unlinkedMentions' ? d.hits : [];
  }
  async graph(filter?: GraphFilter): Promise<GraphData> {
    const d = await this.call({ type: 'graph', filter });
    return d.type === 'graph' ? d.data : { nodes: [], links: [] };
  }
  async localGraph(id: string, depth: number, filter?: GraphFilter): Promise<GraphData> {
    const d = await this.call({ type: 'localGraph', id, depth, filter });
    return d.type === 'graph' ? d.data : { nodes: [], links: [] };
  }
  async tags(): Promise<TagNode[]> {
    const d = await this.call({ type: 'tags' });
    return d.type === 'tags' ? d.tree : [];
  }
  async note(id: string): Promise<NoteIndexEntry | null> {
    const d = await this.call({ type: 'note', id });
    return d.type === 'note' ? d.entry : null;
  }
  async suggestLinks(q: string, fromId?: string, limit?: number): Promise<LinkSuggestion[]> {
    const d = await this.call({ type: 'suggestLinks', q, fromId, limit });
    return d.type === 'suggestLinks' ? d.suggestions : [];
  }
}

export const index = new IndexClient();
